import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, CreditCard, Building2, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { useLanguage } from '../context/LanguageContext';
import { paymentsAPI } from '../services/api';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Pricing data (matches backend and translations)
const PLAN_DATA = {
  free: { monthlyUSD: 0, annualUSD: 0 },
  starter: { monthlyUSD: 39, annualUSD: 390 },
  growth: { monthlyUSD: 129, annualUSD: 1290 },
  pro: { monthlyUSD: 299, annualUSD: 2990 },
};

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const { toast } = useToast();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: form, 2: payment info (for org)

  // Read plan and cycle from URL params (e.g. /register?plan=growth&cycle=annual)
  const urlPlan = (searchParams.get('plan') || 'starter').toLowerCase();
  const urlCycle = (searchParams.get('cycle') || 'monthly').toLowerCase();
  const selectedPlan = PLAN_DATA[urlPlan] ? urlPlan : 'starter';
  const selectedCycle = ['monthly', 'annual'].includes(urlCycle) ? urlCycle : 'monthly';

  const planInfo = PLAN_DATA[selectedPlan];
  const displayPrice = selectedCycle === 'annual' ? planInfo.annualUSD : planInfo.monthlyUSD;

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    role: 'applicant',
    organization: ''
  });

  const planLabels = {
    free: { es: 'Plan Gratuito', en: 'Free Plan' },
    starter: { es: 'Plan Starter', en: 'Starter Plan' },
    growth: { es: 'Plan Growth', en: 'Growth Plan' },
    pro: { es: 'Plan Pro', en: 'Pro Plan' },
  };

  const planTaglines = {
    free: { es: 'Prueba la plataforma sin compromiso', en: 'Try the platform with no commitment' },
    starter: { es: 'Ideal para empezar con innovación abierta', en: 'Ideal to start with open innovation' },
    growth: { es: 'Para organizaciones que quieren más estructura', en: 'For organizations that want more structure' },
    pro: { es: 'Escala y acompañamiento estratégico', en: 'Scale and strategic support' },
  };

  const translations = {
    es: {
      title: 'Crear Cuenta',
      subtitle: 'Únete a ColaboraChile',
      fullName: 'Nombre completo',
      email: 'Correo electrónico',
      accountType: 'Tipo de cuenta',
      applicant: 'Postulante / Emprendedor',
      organization: 'Organización / Empresa',
      evaluator: 'Evaluador',
      orgName: 'Nombre de la organización',
      password: 'Contraseña',
      confirmPassword: 'Confirmar contraseña',
      createAccount: 'Crear Cuenta',
      creatingAccount: 'Creando cuenta...',
      haveAccount: '¿Ya tienes cuenta?',
      signIn: 'Inicia sesión',
      passwordMismatch: 'Las contraseñas no coinciden',
      registrationSuccess: 'Registro exitoso',
      welcome: '¡Bienvenido a ColaboraChile!',
      registrationError: 'Error de registro',
      createError: 'No se pudo crear la cuenta',
      orgSubscription: {
        title: planLabels[selectedPlan]?.es || 'Plan Starter',
        subtitle: planTaglines[selectedPlan]?.es || '',
        price: `USD ${displayPrice}`,
        period: selectedCycle === 'annual' ? '/ año' : '/ mes',
        features: selectedPlan === 'starter' ? [
          'Desafíos ilimitados',
          'Plantillas guiadas para definir tu desafío',
          'Difusión básica en la plataforma',
          'Acceso completo a todas las postulaciones',
          'Dashboard simple de seguimiento',
          'Publicación inmediata'
        ] : selectedPlan === 'growth' ? [
          'Todo lo del plan Starter',
          'Revisión experta de hasta 2 desafíos por año',
          'Destacado en la página de desafíos',
          'Reporting básico de postulaciones',
          'Soporte por email con respuesta en 48 hrs'
        ] : [
          'Todo lo del plan Growth',
          'Curaduría completa de postulaciones',
          'Workshops estratégicos (hasta 2/año)',
          'Soporte dedicado con contacto directo',
          'Reporting avanzado con exportación de datos'
        ],
        paymentInfo: 'Al continuar, serás redirigido a Flow.cl para completar el pago de forma segura.',
        continueToPayment: 'Continuar al Pago',
        back: 'Volver',
        processingPayment: 'Procesando...',
        paymentNote: 'El pago se procesa de forma segura a través de Flow.cl'
      }
    },
    en: {
      title: 'Create Account',
      subtitle: 'Join ColaboraChile',
      fullName: 'Full name',
      email: 'Email',
      accountType: 'Account type',
      applicant: 'Applicant / Entrepreneur',
      organization: 'Organization / Company',
      evaluator: 'Evaluator',
      orgName: 'Organization name',
      password: 'Password',
      confirmPassword: 'Confirm password',
      createAccount: 'Create Account',
      creatingAccount: 'Creating account...',
      haveAccount: 'Already have an account?',
      signIn: 'Sign in',
      passwordMismatch: 'Passwords do not match',
      registrationSuccess: 'Registration successful',
      welcome: 'Welcome to ColaboraChile!',
      registrationError: 'Registration error',
      createError: 'Failed to create account',
      orgSubscription: {
        title: planLabels[selectedPlan]?.en || 'Starter Plan',
        subtitle: planTaglines[selectedPlan]?.en || '',
        price: `USD ${displayPrice}`,
        period: selectedCycle === 'annual' ? '/ year' : '/ month',
        features: selectedPlan === 'starter' ? [
          'Unlimited challenges',
          'Guided templates to define your challenge',
          'Basic platform visibility',
          'Full access to all applications',
          'Simple tracking dashboard',
          'Immediate publication'
        ] : selectedPlan === 'growth' ? [
          'Everything in Starter',
          'Expert review of up to 2 challenges per year',
          'Featured on challenges page',
          'Basic application reporting',
          'Email support with 48hr response'
        ] : [
          'Everything in Growth',
          'Full application curation included',
          'Strategic workshops (up to 2/year)',
          'Dedicated support with direct contact',
          'Advanced reporting with data export'
        ],
        paymentInfo: 'By continuing, you will be redirected to Flow.cl to complete your payment securely.',
        continueToPayment: 'Continue to Payment',
        back: 'Back',
        processingPayment: 'Processing...',
        paymentNote: 'Payment is processed securely through Flow.cl'
      }
    }
  };

  const t = translations[language];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: 'Error',
        description: t.passwordMismatch,
        variant: 'destructive'
      });
      return;
    }

    // If organization role with free plan, register directly
    if (formData.role === 'organization' && selectedPlan === 'free') {
      await handleFreeOrgRegistration();
      return;
    }

    // If organization role with paid plan, show payment step
    if (formData.role === 'organization') {
      setStep(2);
      return;
    }

    // For other roles, proceed with normal registration
    await performRegistration();
  };

  const handleFreeOrgRegistration = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/auth/register-organization`, {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        role: 'organization',
        organization: formData.organization,
        plan: 'free'
      });
      
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      
      toast({
        title: language === 'es' ? 'Bienvenido a ColaboraChile' : 'Welcome to ColaboraChile',
        description: language === 'es' ? 'Tu cuenta gratuita esta activa. Publica tu primer desafio.' : 'Your free account is active. Publish your first challenge.'
      });
      
      // Force page reload to pick up new auth state
      window.location.href = '/organization-dashboard';
    } catch (error) {
      console.error('Free registration error:', error);
      toast({
        title: t.registrationError,
        description: error.response?.data?.detail || t.createError,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const performRegistration = async () => {
    try {
      setLoading(true);
      await register(
        formData.email,
        formData.password,
        formData.name,
        formData.role,
        formData.organization || null
      );
      toast({
        title: t.registrationSuccess,
        description: t.welcome
      });
      navigate('/dashboard');
    } catch (error) {
      console.error('Registration error:', error);
      toast({
        title: t.registrationError,
        description: error.response?.data?.detail || t.createError,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOrganizationPayment = async () => {
    try {
      setLoading(true);
      
      // Step 1: Create user account with pending_payment status
      const registerResponse = await axios.post(`${API_URL}/auth/register-organization`, {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        role: 'organization',
        organization: formData.organization,
        plan: selectedPlan,
        billing_cycle: selectedCycle
      });
      
      const userId = registerResponse.data.user.id;
      
      // Step 2: Create payment order with plan and billing cycle
      const paymentResponse = await paymentsAPI.createOrder({
        user_id: userId,
        email: formData.email,
        name: formData.name,
        organization: formData.organization,
        plan: selectedPlan,
        billing_cycle: selectedCycle
      });
      
      if (paymentResponse.success && paymentResponse.payment_url) {
        toast({
          title: language === 'es' ? 'Redirigiendo al pago...' : 'Redirecting to payment...',
          description: language === 'es' ? 'Serás redirigido a Flow.cl' : 'You will be redirected to Flow.cl'
        });
        
        // Redirect to Flow.cl payment page
        window.location.href = paymentResponse.payment_url;
      } else {
        throw new Error(paymentResponse.message || 'Payment creation failed');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast({
        title: t.registrationError,
        description: error.response?.data?.detail || error.message || t.createError,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, role: value }));
  };

  // Step 2: Payment info for organizations
  if (step === 2) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <Card className="w-full max-w-md border-2 border-primary/20">
          <CardHeader className="text-center bg-gradient-to-r from-primary/10 to-secondary/10 rounded-t-lg">
            <div className="flex justify-center mb-4">
              <div className="bg-primary/10 p-4 rounded-full">
                <Building2 className="h-10 w-10 text-primary" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold">
              {t.orgSubscription.title}
            </CardTitle>
            <CardDescription>
              {t.orgSubscription.subtitle}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            {/* Price display */}
            <div className="text-center py-4 bg-gray-50 rounded-lg">
              <div className="flex items-baseline justify-center">
                <span className="text-4xl font-bold text-primary">{t.orgSubscription.price}</span>
                <span className="text-gray-500 ml-1">{t.orgSubscription.period}</span>
              </div>
            </div>
            
            {/* Features list */}
            <ul className="space-y-3">
              {t.orgSubscription.features.map((feature, index) => (
                <li key={index} className="flex items-center text-gray-600">
                  <svg className="h-5 w-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>

            {/* User info summary */}
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <p className="text-sm text-gray-500">{t.orgName}</p>
              <p className="font-semibold">{formData.organization}</p>
              <p className="text-sm text-gray-500 mt-2">{t.email}</p>
              <p className="font-semibold">{formData.email}</p>
            </div>

            {/* Payment info alert */}
            <Alert>
              <CreditCard className="h-4 w-4" />
              <AlertDescription>
                {t.orgSubscription.paymentInfo}
              </AlertDescription>
            </Alert>
          </CardContent>
          <CardFooter className="flex flex-col space-y-3">
            <Button
              onClick={handleOrganizationPayment}
              disabled={loading}
              className="w-full bg-primary text-white hover:bg-primary/90"
            >
              {loading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t.orgSubscription.processingPayment}</>
              ) : (
                <><CreditCard className="mr-2 h-4 w-4" /> {t.orgSubscription.continueToPayment}</>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setStep(1)}
              disabled={loading}
              className="w-full"
            >
              {t.orgSubscription.back}
            </Button>
            <p className="text-xs text-gray-500 text-center">
              {t.orgSubscription.paymentNote}
            </p>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Step 1: Registration form
  return (
    <div className="min-h-screen bg-sand-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md border-2 border-gray-200">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-bold">
            {t.title}
          </CardTitle>
          <CardDescription>
            {t.subtitle}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t.fullName}</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="border-2 border-gray-200"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t.email}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="border-2 border-gray-200"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">{t.accountType}</Label>
              <Select value={formData.role} onValueChange={handleSelectChange}>
                <SelectTrigger className="border-2 border-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="applicant">
                    {t.applicant}
                  </SelectItem>
                  <SelectItem value="organization">
                    {t.organization} - USD {displayPrice}/{selectedCycle === 'annual' ? (language === 'es' ? 'año' : 'year') : (language === 'es' ? 'mes' : 'month')}
                  </SelectItem>
                  <SelectItem value="evaluator">
                    {t.evaluator}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.role === 'organization' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="organization">{t.orgName}</Label>
                  <Input
                    id="organization"
                    name="organization"
                    value={formData.organization}
                    onChange={handleInputChange}
                    required
                    className="border-2 border-gray-200"
                  />
                </div>
                <Alert className="bg-primary/5 border-primary/20">
                  <AlertCircle className="h-4 w-4 text-primary" />
                  <AlertDescription className="text-sm">
                    {language === 'es' 
                      ? `Cuenta de Organización: Plan ${selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} — USD ${displayPrice}/${selectedCycle === 'annual' ? 'año' : 'mes'}.`
                      : `Organization account: ${selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} Plan — USD ${displayPrice}/${selectedCycle === 'annual' ? 'year' : 'month'}.`
                    }
                  </AlertDescription>
                </Alert>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="password">{t.password}</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                className="border-2 border-gray-200"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t.confirmPassword}</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
                className="border-2 border-gray-200"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-secondary text-white hover:bg-secondary/90"
            >
              {loading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t.creatingAccount}</>
              ) : (
                formData.role === 'organization' 
                  ? (language === 'es' ? 'Continuar al Pago' : 'Continue to Payment')
                  : t.createAccount
              )}
            </Button>

            <div className="text-center text-sm text-gray-600 mt-4">
              {t.haveAccount}{' '}
              <Link to="/login" className="text-primary hover:underline font-semibold">
                {t.signIn}
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
